import type { UploadType } from '@/api';
import FileUploadModal from '@/components/file-upload/file-upload-modal';
import RequiredFormatTooltip from '@/components/file-upload/required-format-tooltip';
import UploadDropZone from '@/components/file-upload/upload-drop-zone';
import { useErrorNotification } from '@/hooks/use-error-notification';
import {
  useGetPresignedPutUrl,
  useGetTemplate,
  useGetUploadReport,
} from '@/pages/queries';
import { normalizeApiError } from '@/utils/error-utils';
import { Icon, TextLink } from '@lego/connect-components-react';
import { useEffect, useState } from 'react';

type PriceUploadResult = {
  updatedProductsCount?: number;
  updatedPriceListsCount?: number;
  rowsAdded?: number;
};

type UploadModalConfig = {
  title: string;
  uploadSubject: string;
  target: string;
  requiredColumns: string[];
  successMessage: (
    uploadResult: PriceUploadResult | undefined,
  ) => React.ReactNode;
};

type FileUploadModalProps = {
  isOpen: boolean;
  triggerRef: React.RefObject<HTMLElement | null>;
  type: UploadType;
  onClose: () => void;
};

export const CONFIGS: Record<UploadType, UploadModalConfig> = {
  Rrp: {
    title: 'Upload LEL prices',
    uploadSubject: 'LEL prices',
    target: 'price_event_store',
    requiredColumns: [
      'Price List',
      'Product ID',
      'RRP Price',
      'Valid From Date',
    ],
    successMessage: (uploadResult) => (
      <>
        You've updated <b>{uploadResult?.updatedProductsCount ?? 0}</b> products
        and <b>{uploadResult?.updatedPriceListsCount ?? 0}</b> price lists
      </>
    ),
  },
  Cost: {
    title: 'Upload materials costs',
    uploadSubject: 'materials costs',
    target: 'licensing_materials_costs',
    requiredColumns: ['Material ID', 'Cost', 'Lot Size', 'Year'],
    successMessage: (uploadResult) => (
      <>
        <b>{uploadResult?.rowsAdded ?? 0}</b>{' '}
        {uploadResult?.rowsAdded === 1 ? 'row' : 'rows'} added
      </>
    ),
  },
  ValueBasedPrice: {
    title: 'Upload Value Based Prices',
    uploadSubject: 'Value Based Prices (VBP)',
    target: 'value_based_prices',
    requiredColumns: ['Product ID', 'Value Based Price'],
    successMessage: (uploadResult) => (
      <>
        <b>{uploadResult?.rowsAdded ?? 0}</b>{' '}
        {uploadResult?.rowsAdded === 1 ? 'row' : 'rows'} added
      </>
    ),
  },
};

const UploadModal = ({
  isOpen,
  triggerRef,
  type,
  onClose,
}: FileUploadModalProps) => {
  const showErrorNotification = useErrorNotification();
  const { mutateAsync } = useGetUploadReport(type);
  const { mutateAsync: getPresignedPutUrl } = useGetPresignedPutUrl(
    CONFIGS[type].target,
  );
  const [uploadResult, setUploadResult] = useState<
    PriceUploadResult | undefined
  >(undefined);
  const {
    data: templateData,
    refetch: refetchTemplate,
    isLoading: isTemplateLoading,
  } = useGetTemplate(CONFIGS[type].target);

  // Prefetch the template whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    refetchTemplate().then(({ data: refetchedTemplateData, error }) => {
      if (error || !refetchedTemplateData?.downloadUrl) {
        showErrorNotification(
          normalizeApiError(
            error ??
              new Error('Could not download the template. Please try again.'),
          ),
          'download template',
        );
      }
    });
  }, [isOpen, refetchTemplate, showErrorNotification]);

  return (
    <FileUploadModal
      isOpen={isOpen}
      triggerRef={triggerRef}
      title={CONFIGS[type].title}
      target={CONFIGS[type].target}
      getPresignedPutUrl={getPresignedPutUrl}
      getReport={async (fileId: string) => {
        const response = await mutateAsync(fileId);
        setUploadResult({
          updatedProductsCount: response.uploadResult?.updatedProductsCount,
          updatedPriceListsCount: response.uploadResult?.updatedPriceListsCount,
          rowsAdded: response.rowsAdded ?? undefined,
        });
        return response.fileProcessResult;
      }}
      contentHeight="340px"
      successMessage={CONFIGS[type].successMessage(uploadResult)}
      onClose={onClose}
      onUploadFinished={() => {}}
      renderIdle={({ onBrowseClick }) => (
        <>
          <span className="body-md-regular">
            Use the provided{' '}
            <TextLink
              href={templateData?.downloadUrl ?? '#'}
              variant="emphasis"
              size="medium"
              download={!!templateData?.downloadUrl}
              disabled={isTemplateLoading || !templateData?.downloadUrl}
              onClick={
                templateData?.downloadUrl
                  ? undefined
                  : (e) => e.preventDefault()
              }
              style={{ textDecoration: 'none' }}
            >
              excel template{' '}
              <Icon
                name="arrow-down-to-line"
                size="small"
                aria-label="download"
                isChild
              />
            </TextLink>{' '}
            to upload {CONFIGS[type].uploadSubject}. Files must match the{' '}
            <RequiredFormatTooltip columns={CONFIGS[type].requiredColumns} />.
          </span>
          <UploadDropZone onBrowseClick={onBrowseClick} />
        </>
      )}
    />
  );
};

export default UploadModal;
